<template>
  <PageLayout
    :page-title="categoryLabel"
    :breadcrumb="$t('nav.news')"
    breadcrumb-to="/news/"
    banner-key="news"
  >
    <div class="nd-view" v-if="news.id || news.title">
      <article class="nd-view__main wrap">
        <header class="nd-view__head">
          <h1 class="nd-view__title" style="text-align:center;width:100%;display:block;margin:0 auto 16px;">{{ displayTitle }}</h1>
        </header>

        <div
          v-if="bodyHtml"
          class="nd-view__body"
          :class="{ 'is-long': isLongBody }"
          v-html="bodyHtml"
        ></div>

        <nav class="nd-view__nav">
          <router-link
            v-if="prevNews"
            :to="newsLink(prevNews)"
            class="nd-view__nav-link nd-view__nav-link--prev"
          >
            <i class="font_family icon-zuojiantou"></i>
            <span>{{ truncateTitle(prevNews.title) }}</span>
          </router-link>
          <span v-else class="nd-view__nav-link is-empty"></span>

          <router-link :to="'/news/' + newsSlug + '/'" class="nd-view__back">
            {{ $t('common.backToList') }}
          </router-link>

          <router-link
            v-if="nextNews"
            :to="newsLink(nextNews)"
            class="nd-view__nav-link nd-view__nav-link--next"
          >
            <span>{{ truncateTitle(nextNews.title) }}</span>
            <i class="font_family icon-youjiantou"></i>
          </router-link>
          <span v-else class="nd-view__nav-link is-empty"></span>
        </nav>

        <section class="nd-view__related" v-if="recommended.length">
          <h2 class="nd-view__related-title">{{ $t('news.recommended') }}</h2>
          <div class="nd-view__related-grid">
            <router-link
              v-for="item in recommended"
              :key="item.id"
              :to="newsLink(item)"
              class="nd-view__related-item"
            >
              <span class="nd-view__related-media">
                <img v-if="item.image" :src="$media(item.image)" :alt="item.title">
              </span>
              <span class="nd-view__related-name">{{ item.title }}</span>
              <span class="nd-view__related-date">{{ formatDate(item.date) }}</span>
            </router-link>
          </div>
        </section>
      </article>
    </div>
  </PageLayout>
</template>

<script>
import PageLayout from '@/components/hirays/PageLayout.vue'
import { getNews, listNews } from '@/utils/http'
import { unwrapData, unwrapRows, mapNewsItem, getNewsTypeBySlug, getNewsSlugByType, stripHtml } from '@/utils/content'
import { fetchSiteNav } from '@/utils/siteNav'
import { NEWS_NAV } from '@/constants/hiraysData'
import { setPageTitle } from '@/utils/pageTitle'

export default {
  components: { PageLayout },
  props: { newsSlug: String, id: [String, Number] },
  data() {
    return {
      news: {},
      related: [],
      newsNav: NEWS_NAV.map(n => ({ slug: n.slug, name: n.title, newsType: n.type }))
    }
  },
  computed: {
    categoryLabel() {
      const item = this.newsNav.find(n => n.slug === this.newsSlug)
      return item ? this.$cleanBrand(item.name || item.slug) : this.$t('nav.news')
    },
    recommended() {
      return this.related
        .filter(item => String(item.id) !== String(this.id))
        .slice(0, 4)
    },
    prevNews() {
      const idx = this.related.findIndex(item => String(item.id) === String(this.id))
      return idx > 0 ? this.related[idx - 1] : null
    },
    nextNews() {
      const idx = this.related.findIndex(item => String(item.id) === String(this.id))
      return idx >= 0 && idx < this.related.length - 1 ? this.related[idx + 1] : null
    },
    displayTitle() {
      const title = this.$cleanBrand(this.news.title || '').trim()
      if (title) return title
      // CMS sometimes puts the headline only in content
      const plain = stripHtml(this.news.content || '').trim()
      return plain.length <= 80 ? plain : plain.slice(0, 80) + '…'
    },
    bodyHtml() {
      const raw = this.news.content || ''
      if (!raw) return ''
      const title = stripHtml(this.news.title || '').trim()
      const plain = stripHtml(raw).trim()
      // Same line already shown as the centered title — don't repeat left-aligned
      if (title && plain === title) return ''
      if (!title && plain.length <= 80) return ''
      return this.$cleanBrand(raw)
    },
    isLongBody() {
      return stripHtml(this.bodyHtml || '').trim().length > 120
    }
  },
  watch: {
    id: { immediate: true, handler() { this.load() } },
    newsSlug() { this.loadRelated() },
    'news.title'(title) {
      const clean = this.$cleanBrand(title || '')
      if (clean) setPageTitle(clean)
    }
  },
  created() {
    this.loadNav()
  },
  methods: {
    reloadCmsData() {
      this.loadNav()
      this.load()
    },

    async loadNav() {
      try {
        const nav = await fetchSiteNav(false, this.$i18n.locale)
        if (nav && nav.newsNav && nav.newsNav.length) this.newsNav = nav.newsNav
      } catch (e) { /* fallback */ }
    },

    newsLink(item) {
      const slug = item.category || this.newsSlug || getNewsSlugByType(item.type)
      return '/news/' + slug + '/' + item.id + '.html'
    },

    truncateTitle(title, max = 28) {
      const text = title || ''
      return text.length > max ? text.substring(0, max) + '...' : text
    },

    formatDate(date) {
      if (!date) return ''
      return String(date).replace(/-/g, '.').substring(0, 10)
    },

    async load() {
      if (!this.id) return
      try {
        const res = await getNews(this.id)
        const data = unwrapData(res)
        if (data && data.id) {
          this.news = mapNewsItem(data)
          await this.loadRelated()
          return
        }
      } catch (e) {
        // no CMS article
      }
      this.news = {}
      this.related = []
    },

    async loadRelated() {
      const params = { status: 1, pageNum: 1, pageSize: 20 }
      const type = getNewsTypeBySlug(this.newsSlug)
      if (type != null) params.type = type
      try {
        const res = await listNews(params, this.$i18n.locale)
        const rows = unwrapRows(res).map(mapNewsItem)
        this.related = rows.length
          ? rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
          : []
      } catch (e) {
        this.related = []
      }
    }
  }
}
</script>
